// Context de Autenticación - Maneja el estado de autenticación global
// Proporciona funciones y estado de autenticación a toda la aplicación

import { createContext, useState, useEffect } from 'react'
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { auth, db } from '../services/firebase.config'
import { getRole } from '../services/roles.service'
import { FIXED_ROLES } from '../constants/permissions'
import { notifyWelcome } from '../services/notifications.service'
import { initAuditLogger, logAuditEvent, setAuditLoggerUser } from '../services/auditLogs.service'

// Crear el contexto de autenticación
const AuthContext = createContext(null)

/**
 * Provider de Autenticación
 * Envuelve la aplicación y proporciona el estado de autenticación
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Componentes hijos
 */
export function AuthProvider({ children }) {
  // Estado del usuario actual
  const [currentUser, setCurrentUser] = useState(null)
  // Estado de carga (para saber si Firebase está verificando la sesión)
  const [loading, setLoading] = useState(true)
  // Estado de los datos del usuario en Firestore
  const [userData, setUserData] = useState(null)
  // Estado del rol personalizado del usuario (si aplica)
  const [customRole, setCustomRole] = useState(null)

  /**
   * Función para registrar un nuevo usuario
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @param {string} displayName - Nombre a mostrar (opcional)
   * @returns {Promise} Promesa que se resuelve cuando el usuario se registra
   */
  const register = async (email, password, displayName = '') => {
    try {
      // Crear el usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const user = userCredential.user

      // Actualizar el perfil con el nombre si se proporciona
      if (displayName) {
        await updateProfile(user, { displayName })
      }

      // Crear documento del usuario en Firestore
      const userDoc = {
        email: user.email,
        displayName: displayName || user.displayName || '',
        role: 'user', // Rol por defecto
        createdAt: new Date().toISOString(),
        emailVerified: user.emailVerified
      }

      // Guardar en Firestore
      await setDoc(doc(db, 'users', user.uid), userDoc)
      notifyWelcome(user.uid).catch(() => null)
      logAuditEvent({
        level: 'info',
        category: 'auth',
        action: 'register_success',
        message: `Registro exitoso para ${user.email || email}`,
        metadata: { uid: user.uid }
      })

      return { success: true, user }
    } catch (error) {
      console.error('Error al registrar usuario:', error)
      logAuditEvent({
        level: 'error',
        category: 'auth',
        action: 'register_error',
        message: `Error en registro para ${email}`,
        error
      })
      return { 
        success: false, 
        error: error.message,
        code: error.code
      }
    }
  }

  /**
   * Función para iniciar sesión
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise} Promesa que se resuelve cuando el usuario inicia sesión
   */
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      logAuditEvent({
        level: 'info',
        category: 'auth',
        action: 'login_success',
        message: `Login exitoso para ${email}`,
        metadata: { uid: userCredential.user?.uid || null }
      })
      return { success: true, user: userCredential.user }
    } catch (error) {
      console.error('Error al iniciar sesión:', error)
      logAuditEvent({
        level: 'error',
        category: 'auth',
        action: 'login_error',
        message: `Error al iniciar sesión para ${email}`,
        error
      })
      return { 
        success: false, 
        error: error.message,
        code: error.code
      }
    }
  }

  /**
   * Función para cerrar sesión
   * @returns {Promise} Promesa que se resuelve cuando se cierra la sesión
   */
  const logout = async () => {
    try {
      await signOut(auth)
      setUserData(null)
      logAuditEvent({
        level: 'info',
        category: 'auth',
        action: 'logout_success',
        message: 'Cierre de sesión exitoso'
      })
      return { success: true }
    } catch (error) {
      console.error('Error al cerrar sesión:', error)
      logAuditEvent({
        level: 'error',
        category: 'auth',
        action: 'logout_error',
        message: 'Error al cerrar sesión',
        error
      })
      return { 
        success: false, 
        error: error.message
      }
    }
  }

  /**
   * Función para enviar email de recuperación de contraseña
   * @param {string} email - Email del usuario
   * @returns {Promise} Promesa que se resuelve cuando se envía el email
   */
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email)
      return { success: true }
    } catch (error) {
      console.error('Error al enviar email de recuperación:', error)
      return { 
        success: false, 
        error: error.message,
        code: error.code
      }
    }
  }

  /**
   * Función para cargar los datos del usuario desde Firestore
   * @param {string} uid - ID del usuario
   */
  const loadUserData = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid)
      const userDocSnap = await getDoc(userDocRef)
      
      if (userDocSnap.exists()) {
        const data = userDocSnap.data()
        setUserData(data)
        
        // Si el usuario tiene un roleId (rol personalizado), cargar el rol
        if (data.roleId && data.role !== FIXED_ROLES.SUPERADMIN && data.role !== FIXED_ROLES.ADMIN && data.role !== FIXED_ROLES.CLIENT) {
          try {
            const role = await getRole(data.roleId)
            setCustomRole(role)
          } catch (error) {
            console.error('Error cargando rol personalizado:', error)
            setCustomRole(null)
          }
        } else {
          setCustomRole(null)
        }
      } else {
        console.warn('No se encontraron datos del usuario en Firestore')
        setUserData(null)
        setCustomRole(null)
      }
    } catch (error) {
      console.error('Error al cargar datos del usuario:', error)
      setUserData(null)
      setCustomRole(null)
    }
  }

  // Efecto para escuchar cambios en el estado de autenticación
  useEffect(() => {
    initAuditLogger()
    // Suscribirse a los cambios de autenticación
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user)
      
      // Si hay un usuario, cargar sus datos de Firestore
      if (user) {
        await loadUserData(user.uid)
      } else {
        setUserData(null)
      }
      
      // Marcar como cargado cuando termine la verificación
      setLoading(false)
    })

    // Limpiar la suscripción cuando el componente se desmonte
    return unsubscribe
  }, [])

  useEffect(() => {
    setAuditLoggerUser({ currentUser, userData })
  }, [currentUser, userData])

  // Valor que se proporciona a través del contexto
  const value = {
    currentUser,      // Usuario actual de Firebase Auth
    userData,         // Datos adicionales del usuario desde Firestore
    customRole,       // Rol personalizado del usuario (si aplica)
    loading,          // Estado de carga
    register,         // Función para registrar
    login,            // Función para iniciar sesión
    logout,           // Función para cerrar sesión
    resetPassword,    // Función para recuperar contraseña
    loadUserData      // Función para recargar datos del usuario
  }

  return (
    <AuthContext.Provider value={value}>
      {/* No renderizar los hijos hasta que se verifique la autenticación */}
      {!loading && children}
    </AuthContext.Provider>
  )
}

export default AuthContext

